/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useState } from "react"
import { Drawer, Button, Table } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { useSelector } from "react-redux"
import { get, set, isEmpty } from "lodash"
import { AppState } from "store/store.type"
import {
  days,
  daysMap,
  mapSectionObj,
  mapServiceDay,
  cleanServiceHours,
  DaysText,
  OpenPeriodText,
} from "utils/section_helper"
import { Section } from "types/models/menu"
import { OverflowTip } from "components/common"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { CreateSectionRequest, SectionData, ServiceDay } from "types/api"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { ChevronLeftUniversal } from "@duxton-web/icons"
import { TimingSelector } from "./TimingSelector"

import {
  drawerBodyStyle,
  drawerContentStyle,
  drawerFooterStyle,
} from "../../styles"

const scrollbarStyle = css`
  &::-webkit-scrollbar {
    width: 20px;
    background-color: rgba(0, 0, 0, 0.15);
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.25);
  }
`

const renderColumnTitle = (title: string) => (
  <div
    css={css`
      max-width: 150px;
    `}
  >
    <OverflowTip>{title}</OverflowTip>
  </div>
)

interface IProps extends FormComponentProps {
  section: Section | null
  form: WrappedFormUtils
  isEdit: boolean
  visible: boolean
  onClose(e?: any): void
  onSave?(data: CreateSectionRequest): void
}

export const SectionTimingsDrawerComponent: FC<IProps> = ({
  isEdit,
  section,
  visible,
  onClose,
  onSave,
  form,
}: IProps) => {
  const [propagateServiceDays, setPropagateServiceDays] = useState<{
    [availableStatus: string]: ServiceDay | null | undefined
  }>({})
  const [fieldsTouched, setFieldsTouched] = useState(false)

  const {
    merchantID,
    menu: { sections },
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
  } = useSelector((state: AppState) => state?.menu)

  const { getFieldDecorator, getFieldValue } = form

  const otherSectionsToUpdate =
    sections?.filter(
      _section => !isEdit || section?.sectionID !== _section?.sectionID,
    ) || []

  const prefixes = [
    "sectionToCreate",
    "sectionToEdit",
    ...otherSectionsToUpdate.map((_, index) => `sectionsToUpdate.${index}`),
  ]

  const handleSave = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickSetTiming,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SECTION_ID: section?.sectionID,
        INTENT: isEdit ? "EDIT_SECTION" : "ADD_SECTION",
      },
    })
    form.validateFields(async (err, values) => {
      if (err) return
      const cleanValues: CreateSectionRequest = {
        ...values,
        sectionsToUpdate: values.sectionsToUpdate?.filter(
          (obj: any) => !isEmpty(obj),
        ),
      }

      prefixes.forEach(prefix => {
        const s: SectionData = get(cleanValues, prefix)

        if (!s) return

        set(
          cleanValues,
          `${prefix}.serviceHours`,
          cleanServiceHours(s.serviceHours ?? {}),
        )
      })

      onSave?.(cleanValues)
      onClose()
    })
  }

  const handleApplyToAll = (day: string) => {
    const newState = { ...propagateServiceDays }
    prefixes.forEach(prefix => {
      const s: SectionData = getFieldValue(prefix)
      const valueToApply = s?.serviceHours?.[day]
      newState[prefix] = valueToApply
    })
    setPropagateServiceDays(newState)
  }

  const isFirstSection =
    sections?.length === 0 || (isEdit && sections?.length === 1)

  const openPeriodText = OpenPeriodText()

  const textMap = useTranslationsMap({
    timingAdd: "menu.drawers.section.timingAdd",
    timingApply: "menu.drawers.section.timingApply",
  })

  const renderSectionCell = (prefix: string, _section?: Section | null) => (
    _: any,
    { day }: { day: string },
  ) => {
    let sectionObj: SectionData | null = null
    if (_section) sectionObj = mapSectionObj(_section)
    return (
      <div>
        {_section && sectionObj && (
          <Fragment>
            {Object.keys(sectionObj).map((fieldName: string) => (
              <div key={fieldName}>
                {getFieldDecorator(`${prefix}.${fieldName}`, {
                  initialValue: get(sectionObj, fieldName),
                })(<input type="hidden" />)}
              </div>
            ))}
          </Fragment>
        )}
        <TimingSelector
          data-testid="timing-selector"
          openPeriodText={openPeriodText}
          serviceHour={mapServiceDay(
            _section?.openingHours?.[daysMap.indexOf(day)] ?? { ranges: [] },
          )}
          prefix={`${prefix}.serviceHours.${day}`}
          form={form}
          day={day}
          propagateValue={propagateServiceDays[prefix]}
          disabled={isFirstSection}
          isFirstSection={isFirstSection}
          onChange={() => setFieldsTouched(true)}
          textMap={textMap}
        />
      </div>
    )
  }

  const daysText = DaysText()

  const handleClose = () => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewTab,
      action: EventAction.clickExitEditTiming,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SECTION_ID: section?.sectionID,
        INTENT: isEdit ? "EDIT_SECTION" : "ADD_SECTION",
      },
    })

    onClose()
  }

  return (
    <Drawer
      destroyOnClose
      data-testid="drawer"
      title={useTranslation("menu.drawers.section.buttons.setTimings")}
      width={645}
      onClose={handleClose}
      open={visible}
      closeIcon={<ChevronLeftUniversal />}
    >
      <div css={drawerBodyStyle}>
        <div
          css={css`
            flex: 1 0 auto;
          `}
        >
          <div
            css={css`
              ${drawerContentStyle}
              padding:0;
              height: 100%;
            `}
          >
            <Table
              css={css`
                .dui-table {
                  border: none !important;
                  border-radius: unset;
                }
                .dui-table-content {
                  ${scrollbarStyle}
                  td {
                    vertical-align: top;
                  }
                }
              `}
              rowKey="day"
              pagination={false}
              dataSource={days.map(day => ({
                day: day.toLowerCase(),
              }))}
              scroll={{ x: "max-content" }}
            >
              <Table.Column
                data-testid="day-column"
                dataIndex="day"
                key="day"
                render={(day: string) => daysText[day]}
                title={null}
                width={92}
              />
              {otherSectionsToUpdate.map((_section, index) => (
                <Table.Column
                  key={_section?.sectionID}
                  title={renderColumnTitle(_section?.sectionName)}
                  render={renderSectionCell(
                    `sectionsToUpdate.${index}`,
                    _section,
                  )}
                />
              ))}
              {!isEdit && section?.sectionName && (
                <Table.Column
                  data-testid="section-to-create-column"
                  key="sectionToCreate"
                  title={renderColumnTitle(section.sectionName)}
                  render={renderSectionCell("sectionToCreate", section)}
                />
              )}
              {isEdit && section?.sectionName && (
                <Table.Column
                  data-testid="section-to-edit-column"
                  key="sectionToEdit"
                  title={renderColumnTitle(section.sectionName)}
                  render={renderSectionCell("sectionToEdit", section)}
                />
              )}
              {!isFirstSection && (
                <Table.Column
                  data-testid="actions-column"
                  key="actions"
                  className="actions"
                  render={(_: any, { day }: { day: string }, index: number) =>
                    index === 0 && (
                      <Button type="link" onClick={() => handleApplyToAll(day)}>
                        {textMap.timingApply}
                      </Button>
                    )
                  }
                />
              )}
            </Table>
          </div>
        </div>

        <div css={drawerFooterStyle}>
          <Button
            data-testid="save-button"
            type="primary"
            block
            onClick={handleSave}
            disabled={isEdit && !fieldsTouched}
          >
            {useTranslation("menu.drawers.section.buttons.setTimings")}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export const SectionTimingsDrawer = Form.create<IProps>({})(
  SectionTimingsDrawerComponent,
)
